import axios from 'axios';
import { OpenAPI } from './api/core/OpenAPI';
import { store } from './store/store';
import { clearUserInfo } from './store/userSlice';

// Osobna instancja axios do refresh/logout — bez interceptorów, aby uniknąć pętli
const plainClient = axios.create();

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve();
        }
    });
    failedQueue = [];
};

const forceLogout = async () => {
    const state = store.getState();
    if (!state.user.isAuthenticated) return;

    store.dispatch(clearUserInfo());

    // Wywołaj /logout aby wyczyścić HttpOnly cookies po stronie backendu
    // Bez tego restoreSession() po przeładowaniu strony ponownie zaloguje użytkownika
    try {
        await plainClient.post(`${OpenAPI.BASE}/logout`, null, {
            withCredentials: true,
        });
    } catch (_) {
        // Backend może być niedostępny — ustaw flagę jako fallback
        // AuthContext sprawdzi tę flagę i nie odtworzy sesji
        sessionStorage.setItem('forceLogout', '1');
    }

    window.location.href = '/login';
};

export const configureAPI = () => {
    OpenAPI.BASE = (process.env.REACT_APP_API_URL as string) || '/api';
    OpenAPI.WITH_CREDENTIALS = true;
    OpenAPI.CREDENTIALS = 'include';

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Nie przechwytuj jeśli: nie 401, już ponawiane, lub to publiczny endpoint
            if (
                error.response?.status !== 401 ||
                originalRequest._retry ||
                originalRequest.url?.includes('/refresh') ||
                originalRequest.url?.includes('/login') ||
                originalRequest.url?.includes('/register')
            ) {
                return Promise.reject(error);
            }

            // Jeśli refresh już trwa — dodaj do kolejki i czekaj
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    originalRequest._retry = true;
                    return axios(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                await plainClient.post(`${OpenAPI.BASE}/refresh`, null, {
                    withCredentials: true,
                });

                // Refresh udany — ponów wszystkie oczekujące requesty
                processQueue(null);
                return axios(originalRequest);
            } catch (refreshError) {
                // Refresh nieudany — odrzuć wszystkie oczekujące requesty
                processQueue(refreshError);

                await forceLogout();

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
    );

};
